/**
 * InspectionList — the "queue" tab of the Cleaning Inspection page.
 *
 * Pure presentational: takes the filtered inspection array plus the parent
 * page's state setters / handler functions and renders the list rows with
 * action buttons (Start / Continue / View / Report / Email / Approve / Edit
 * / Delete). Lives in /components/inspections/ so the page component stays
 * focused on data fetching and global state.
 */
import {
  Building, MapPin, Calendar, Sparkles, ClipboardCheck, Eye, Download, Send,
  Check, Edit, Trash,
} from 'lucide-react';

/**
 * Soft-tile action button — matches the Edit/Delete look already used in
 * this file. All row actions share this component so the row is scannable.
 * Compact icon-only style when `label` is omitted.
 */
const InspectionAction = ({ testid, onClick, title, icon: Icon, label, tint }) => {
  const bg = `${tint}1F`;   // ~12% opacity
  const hoverBg = `${tint}33`; // ~20% opacity
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      title={title || label}
      aria-label={title || label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: label ? 6 : 0,
        padding: label ? '7px 12px' : 0,
        width: label ? 'auto' : 34, height: 34,
        background: bg, color: tint,
        border: 'none', borderRadius: 9, cursor: 'pointer',
        fontSize: 13, fontWeight: 600,
        transition: 'background 0.15s ease, transform 0.15s ease',
        justifyContent: 'center',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = hoverBg;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = bg;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Icon size={16} strokeWidth={2.25} />
      {label && <span>{label}</span>}
    </button>
  );
};

export default function InspectionList({
  filteredInspections,
  inspectionChecklist,
  user,
  calculateProgress,
  calculateScore,
  getStatusBadge,
  startInspection,
  approveInspection,
  editInspection,
  generatePDFReport,
  setSelectedInspection,
  setShowInspectionModal,
  setExpandedCategories,
  setShowReportPreview,
  setEmailAddress,
  setShowEmailModal,
  setShowDeleteConfirm,
}) {
  return (
    <div
      data-testid="inspection-list"
      style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', letterSpacing: 0.1 }}>
          Inspection Queue <span style={{ color: '#94a3b8', fontWeight: 500 }}>({filteredInspections.length})</span>
        </h2>
      </div>

      {filteredInspections.length === 0 ? (
        <div data-testid="inspection-list-empty" style={{ padding: '60px 20px', textAlign: 'center', color: '#6b7280' }}>
          <ClipboardCheck size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
          <p>No inspections found</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            Inspections are auto-created from scheduled appointments
          </p>
        </div>
      ) : (
        <div>
          {filteredInspections.map((inspection) => (
            <div
              key={inspection.id}
              data-testid={`inspection-row-${inspection.id}`}
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f3f4f6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Building size={16} color="#6b7280" />
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{inspection.customerName}</span>
                  {getStatusBadge(inspection.status)}
                  {inspection.customerSignature && (
                    <span style={{ background: '#d1fae5', color: '#059669', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                      ✓ Signed
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#6b7280' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MapPin size={14} /> {inspection.address}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={14} /> {new Date(inspection.scheduledDate).toLocaleDateString()}
                  </span>
                </div>
                {inspection.status !== 'pending' && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{
                      height: '6px',
                      background: '#e5e7eb',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      width: '200px',
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${calculateProgress(inspection)}%`,
                        background: calculateProgress(inspection) === 100 ? '#10b981' : '#3b82f6',
                        transition: 'width 0.3s',
                      }} />
                    </div>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {calculateProgress(inspection)}% complete
                      {calculateScore(inspection) !== null && ` • Score: ${calculateScore(inspection)}%`}
                    </span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {inspection.status === 'pending' && (
                  <InspectionAction
                    testid={`inspection-start-${inspection.id}`}
                    onClick={() => startInspection(inspection)}
                    title="Start inspection"
                    icon={Sparkles}
                    label="Start"
                    tint="#0073ea"
                  />
                )}
                {inspection.status === 'in_progress' && (
                  <InspectionAction
                    testid={`inspection-continue-${inspection.id}`}
                    onClick={() => {
                      setSelectedInspection(inspection);
                      setShowInspectionModal(true);
                      const expanded = {};
                      Object.keys(inspectionChecklist).forEach((cat) => { expanded[cat] = true; });
                      setExpandedCategories(expanded);
                    }}
                    title="Continue inspection"
                    icon={ClipboardCheck}
                    label="Continue"
                    tint="#3b82f6"
                  />
                )}
                {(inspection.status === 'completed' || inspection.status === 'approved') && (
                  <>
                    <InspectionAction
                      testid={`inspection-view-${inspection.id}`}
                      onClick={() => {
                        setSelectedInspection(inspection);
                        setShowReportPreview(true);
                      }}
                      title="View report"
                      icon={Eye}
                      label="View"
                      tint="#64748b"
                    />
                    <InspectionAction
                      testid={`inspection-report-${inspection.id}`}
                      onClick={() => generatePDFReport(inspection)}
                      title="Download PDF"
                      icon={Download}
                      label="Report"
                      tint="#10b981"
                    />
                    <InspectionAction
                      testid={`inspection-email-${inspection.id}`}
                      onClick={() => {
                        setSelectedInspection(inspection);
                        setEmailAddress(inspection.customerEmail || '');
                        setShowEmailModal(true);
                      }}
                      title="Email report"
                      icon={Send}
                      label="Email"
                      tint="#3b82f6"
                    />
                    {inspection.status === 'completed' && user?.role !== 'employee' && (
                      <InspectionAction
                        testid={`inspection-approve-${inspection.id}`}
                        onClick={() => approveInspection(inspection)}
                        title="Approve inspection"
                        icon={Check}
                        label="Approve"
                        tint="#0073ea"
                      />
                    )}
                  </>
                )}

                <InspectionAction
                  testid={`inspection-edit-${inspection.id}`}
                  onClick={() => editInspection(inspection)}
                  title="Edit inspection"
                  icon={Edit}
                  tint="#3b82f6"
                />

                <InspectionAction
                  testid={`inspection-delete-${inspection.id}`}
                  onClick={() => setShowDeleteConfirm(inspection.id)}
                  title="Delete inspection"
                  icon={Trash}
                  tint="#ef4444"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
